import {LOG_REDACTED, LogSafeValue, type LogRedacted} from './logSafe/LogSafeValue';

declare const uuidBrand: unique symbol;

export type UuidString = string & {readonly [uuidBrand]: true};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function logUuid(value: string | null | undefined): UuidString | LogRedacted | null {
    if (value === null || value === undefined) {
        return null;
    }

    return UUID_PATTERN.test(value) ? (value as UuidString) : LOG_REDACTED;
}

type LogSafePrimitive =
    UuidString | LogRedacted | number | boolean | null | undefined | Date | LogSafeValue;

// A pattern such as `${string}` maps to an index signature, which an empty object satisfies; a literal does not.
export type LiteralString<Value extends string> =
    Record<never, never> extends Record<Value, true> ? never : Value;

// A string is admitted only as a finite set of literals (a constant or an enum member), the nearest
// TypeScript equivalent of the backend's literal-string.
type LogContextValue<Value> = Value extends LogSafePrimitive
    ? Value
    : Value extends string
      ? LiteralString<Value>
      : never;

// `any` would satisfy every branch above, and untyped JavaScript stores hand out nothing else.
type IsAny<Value> = 0 extends 1 & Value ? true : false;

export type LogContext<Context> = {
    [Key in keyof Context]: IsAny<Context[Key]> extends true
        ? never
        : LogContextValue<Context[Key]>;
};

// The already-checked form, for a context built in one place and reported in another.
export type LogContextRecord = Record<string, LogSafePrimitive>;

export type SerializedLogContext = Record<string, string | number | boolean | null>;

export function serializeLogContext(context: Record<string, unknown>): SerializedLogContext {
    return Object.fromEntries(
        Object.entries(context)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [key, serializeLogValue(value)])
    );
}

// Default-deny at runtime too, for JavaScript callers the types cannot reach.
function serializeLogValue(value: unknown): string | number | boolean | null {
    if (value instanceof Date) {
        return value.toISOString();
    }

    if (value instanceof LogSafeValue) {
        return value.toString();
    }

    if (
        value === null ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'string'
    ) {
        return value;
    }

    return LOG_REDACTED;
}
